import React, { useState } from 'react';
import { Button, Card, Space, Typography, Upload } from 'antd';
import { UploadOutlined, DownloadOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { Alert } from 'flowbite-react';
import { dispatchService } from '../services/dispatches';

const { Title, Text } = Typography;

export const DispatchImport: React.FC = () => {
  const [importing, setImporting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [submitNotice, setSubmitNotice] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);
  const navigate = useNavigate();

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleImport = async () => {
    if (!selectedFile) {
      setSubmitNotice({ type: 'info', message: 'Please select a file first' });
      return;
    }
    setImporting(true);
    try {
      await dispatchService.import(selectedFile);
      setSubmitNotice({ type: 'success', message: 'Dispatch import completed' });
      setSelectedFile(null);
    } catch {
      setSubmitNotice({ type: 'error', message: 'Failed to import dispatches' });
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await dispatchService.downloadImportTemplate();
      downloadBlob(blob, 'dispatch-import-template.xlsx');
    } catch {
      setSubmitNotice({ type: 'error', message: 'Failed to download dispatch import template' });
    }
  };

  return (
    <div>
      {submitNotice && (
        <div style={{ marginBottom: 16 }}>
          <Alert color={submitNotice.type} onDismiss={() => setSubmitNotice(null)}>
            {submitNotice.message}
          </Alert>
        </div>
      )}
      <Space
        style={{
          marginBottom: 16,
          width: '100%',
          justifyContent: 'flex-end',
        }}
      >
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/dispatches')}>
          Back
        </Button>
      </Space>
      <Card>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Title level={3} style={{ margin: 0 }}>
            Dispatch Import
          </Title>
          <Text>
            Upload an Excel file to import dispatches. Use the template to match required columns.
          </Text>
          <Space>
            <Upload
              accept=".xlsx,.xls"
              maxCount={1}
              fileList={selectedFile ? [{ uid: 'selected', name: selectedFile.name, status: 'done' }] : []}
              beforeUpload={(file) => {
                setSelectedFile(file);
                return false;
              }}
              onRemove={() => {
                setSelectedFile(null);
              }}
            >
              <Button type="primary" icon={<UploadOutlined />} loading={importing}>
                Upload File
              </Button>
            </Upload>
            <Button icon={<DownloadOutlined />} onClick={handleDownloadTemplate}>
              Dispatch Import Template
            </Button>
            <Button
              type="primary"
              onClick={handleImport}
              loading={importing}
              disabled={!selectedFile}
            >
              Submit
            </Button>
          </Space>
        </Space>
      </Card>
    </div>
  );
};
